import { useState, useEffect, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Settings } from 'lucide-react'
import { OUTPUT_FORMAT_OPTIONS } from '../../types/velociraptor'
import type { UpsertCollectorSettingsInput } from '../../types/artefact-templates'
import { useCollectorSettings } from '../../hooks/use-artefact-templates'

const ENCRYPTION_SCHEME_OPTIONS = [
  { value: 'none', labelKey: 'velociraptor.orgTemplates.encryptionNone' },
  { value: 'x509', labelKey: 'velociraptor.orgTemplates.encryptionX509' },
  { value: 'password', labelKey: 'velociraptor.orgTemplates.encryptionPassword' },
] as const

const DEFAULT_COMPRESSION = 5
const MAX_COMPRESSION = 9
const MAX_CPU_PERCENT = 100
const INPUT_CLASS =
  'w-full text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-[#4e5e82] focus:border-transparent disabled:opacity-50'
const SELECT_CLASS =
  'w-full text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-[#4e5e82] focus:border-transparent disabled:opacity-50'

const DEFAULT_SETTINGS: UpsertCollectorSettingsInput = {
  outputFormat: 'zip',
  encryptionScheme: 'none',
  compressionLevel: DEFAULT_COMPRESSION,
  cpuLimit: null,
  iopsLimit: null,
  progressTimeout: null,
  timeout: null,
}

function parseOptionalInt(value: string): number | null {
  const parsed = Number.parseInt(value, 10)
  return Number.isNaN(parsed) ? null : parsed
}

interface CollectorSettingsDialogProps {
  isOpen: boolean
  templateId: string
  templateName: string
  onClose: () => void
  onSaved: () => void
}

export function CollectorSettingsDialog({
  isOpen,
  templateId,
  templateName,
  onClose,
  onSaved,
}: CollectorSettingsDialogProps): ReactElement | null {
  if (!isOpen) return null

  return (
    <DialogContent
      templateId={templateId}
      templateName={templateName}
      onClose={onClose}
      onSaved={onSaved}
    />
  )
}

interface DialogContentProps {
  templateId: string
  templateName: string
  onClose: () => void
  onSaved: () => void
}

interface SettingsFormState {
  settings: import('../../types/artefact-templates').CollectorSettings | null
  loading: boolean
  saving: boolean
  error: string | null
  form: UpsertCollectorSettingsInput
  saveSuccess: boolean
  updateField: <K extends keyof UpsertCollectorSettingsInput>(
    key: K,
    value: UpsertCollectorSettingsInput[K]
  ) => void
  handleSave: () => Promise<void>
  handleReset: () => Promise<void>
}

function useSettingsForm(templateId: string, onSaved: () => void): SettingsFormState {
  const hookResult = useCollectorSettings(templateId)
  const [form, setForm] = useState<UpsertCollectorSettingsInput>(DEFAULT_SETTINGS)
  const [saveSuccess, setSaveSuccess] = useState(false)

  useEffect(() => {
    if (hookResult.settings) {
      const cs = hookResult.settings
      setForm({
        outputFormat: cs.outputFormat,
        encryptionScheme: cs.encryptionScheme,
        compressionLevel: cs.compressionLevel,
        cpuLimit: cs.cpuLimit,
        iopsLimit: cs.iopsLimit,
        progressTimeout: cs.progressTimeout,
        timeout: cs.timeout,
      })
    } else {
      setForm(DEFAULT_SETTINGS)
    }
  }, [hookResult.settings])

  function updateField<K extends keyof UpsertCollectorSettingsInput>(
    key: K,
    value: UpsertCollectorSettingsInput[K]
  ): void {
    setSaveSuccess(false)
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSave(): Promise<void> {
    setSaveSuccess(false)
    const result = await hookResult.save(form)
    if (result) {
      setSaveSuccess(true)
      onSaved()
    }
  }

  async function handleReset(): Promise<void> {
    setSaveSuccess(false)
    const removed = await hookResult.remove()
    if (removed) {
      setForm(DEFAULT_SETTINGS)
      onSaved()
    }
  }

  return { ...hookResult, form, saveSuccess, updateField, handleSave, handleReset }
}

function DialogContent({
  templateId,
  templateName,
  onClose,
  onSaved,
}: DialogContentProps): ReactElement {
  const state = useSettingsForm(templateId, onSaved)

  if (state.loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-xl dark:bg-slate-800">
          <div className="flex items-center justify-center" role="status">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#4e5e82] border-t-transparent" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl dark:bg-slate-800">
        <SettingsHeader templateName={templateName} onClose={onClose} />
        <SettingsFormBody
          form={state.form}
          saving={state.saving}
          error={state.error}
          saveSuccess={state.saveSuccess}
          updateField={state.updateField}
        />
        <SettingsFooter
          saving={state.saving}
          hasSettings={state.settings !== null}
          onReset={() => {
            void state.handleReset()
          }}
          onClose={onClose}
          onSave={() => {
            void state.handleSave()
          }}
        />
      </div>
    </div>
  )
}

function SettingsFormBody({
  form,
  saving,
  error,
  saveSuccess,
  updateField,
}: FieldSectionProps & { error: string | null; saveSuccess: boolean }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="space-y-4 px-6 py-4">
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {t('velociraptor.orgTemplates.settingsDescription')}
      </p>
      <FormatAndEncryption form={form} saving={saving} updateField={updateField} />
      <CompressionSlider form={form} saving={saving} updateField={updateField} />
      <ResourceLimits form={form} saving={saving} updateField={updateField} />
      <TimeoutFields form={form} saving={saving} updateField={updateField} />
      {saveSuccess && (
        <div
          className="rounded-lg bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950/50 dark:text-green-300"
          role="status"
        >
          {t('velociraptor.orgTemplates.settingsSaved')}
        </div>
      )}
      {error && (
        <div
          className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300"
          role="alert"
        >
          {error}
        </div>
      )}
    </div>
  )
}

function SettingsHeader({
  templateName,
  onClose,
}: {
  templateName: string
  onClose: () => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <Settings className="h-5 w-5 text-violet-600" />
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t('velociraptor.orgTemplates.settingsTitle')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">{templateName}</p>
        </div>
      </div>
      <button
        onClick={onClose}
        className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
        aria-label={t('velociraptor.configModal.close')}
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

interface FieldSectionProps {
  form: UpsertCollectorSettingsInput
  saving: boolean
  updateField: <K extends keyof UpsertCollectorSettingsInput>(
    key: K,
    value: UpsertCollectorSettingsInput[K]
  ) => void
}

function FormatAndEncryption({ form, saving, updateField }: FieldSectionProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
          {t('velociraptor.configModal.outputFormat')}
        </label>
        <select
          value={form.outputFormat ?? 'zip'}
          onChange={(ev) => {
            updateField('outputFormat', ev.target.value)
          }}
          disabled={saving}
          className={SELECT_CLASS}
        >
          {OUTPUT_FORMAT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
          {t('velociraptor.orgTemplates.encryptionScheme')}
        </label>
        <select
          value={form.encryptionScheme ?? 'none'}
          onChange={(ev) => {
            updateField('encryptionScheme', ev.target.value)
          }}
          disabled={saving}
          className={SELECT_CLASS}
        >
          {ENCRYPTION_SCHEME_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

function CompressionSlider({ form, saving, updateField }: FieldSectionProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
        {t('velociraptor.configModal.compressionLevel')}
        <span className="ml-2 font-normal text-slate-400">
          ({form.compressionLevel ?? DEFAULT_COMPRESSION})
        </span>
      </label>
      <input
        type="range"
        min={0}
        max={MAX_COMPRESSION}
        value={form.compressionLevel ?? DEFAULT_COMPRESSION}
        onChange={(ev) => {
          updateField('compressionLevel', Number.parseInt(ev.target.value, 10))
        }}
        disabled={saving}
        className="w-full accent-[#4e5e82]"
      />
      <div className="mt-0.5 flex justify-between text-xs text-slate-400">
        <span>{t('velociraptor.configModal.none')}</span>
        <span>{t('velociraptor.configModal.maximum')}</span>
      </div>
    </div>
  )
}

function ResourceLimits({ form, saving, updateField }: FieldSectionProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <p className="mb-2 text-xs font-medium text-slate-700 dark:text-slate-300">
        {t('velociraptor.configModal.resourceLimits')}
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
            {t('velociraptor.configModal.cpuLimit')}
          </label>
          <input
            type="number"
            min={0}
            max={MAX_CPU_PERCENT}
            value={form.cpuLimit ?? ''}
            onChange={(ev) => {
              updateField('cpuLimit', parseOptionalInt(ev.target.value))
            }}
            disabled={saving}
            placeholder="Unlimited"
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
            {t('velociraptor.configModal.iopsLimit')}
          </label>
          <input
            type="number"
            min={0}
            value={form.iopsLimit ?? ''}
            onChange={(ev) => {
              updateField('iopsLimit', parseOptionalInt(ev.target.value))
            }}
            disabled={saving}
            placeholder="Unlimited"
            className={INPUT_CLASS}
          />
        </div>
      </div>
    </div>
  )
}

function TimeoutFields({ form, saving, updateField }: FieldSectionProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <p className="mb-2 text-xs font-medium text-slate-700 dark:text-slate-300">
        {t('velociraptor.configModal.timeouts')}
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
            {t('velociraptor.configModal.progressTimeout')}
          </label>
          <input
            type="number"
            min={0}
            value={form.progressTimeout ?? ''}
            onChange={(ev) => {
              updateField('progressTimeout', parseOptionalInt(ev.target.value))
            }}
            disabled={saving}
            placeholder="Default"
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
            {t('velociraptor.configModal.overallTimeout')}
          </label>
          <input
            type="number"
            min={0}
            value={form.timeout ?? ''}
            onChange={(ev) => {
              updateField('timeout', parseOptionalInt(ev.target.value))
            }}
            disabled={saving}
            placeholder="Default"
            className={INPUT_CLASS}
          />
        </div>
      </div>
    </div>
  )
}

function SettingsFooter({
  saving,
  hasSettings,
  onReset,
  onClose,
  onSave,
}: {
  saving: boolean
  hasSettings: boolean
  onReset: () => void
  onClose: () => void
  onSave: () => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-700">
      <button
        onClick={onReset}
        disabled={saving || !hasSettings}
        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        {t('velociraptor.orgTemplates.settingsReset')}
      </button>
      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          {t('collections.artefactTemplates.cancel')}
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] focus:ring-2 focus:ring-[#4e5e82] focus:outline-none disabled:opacity-50"
        >
          {saving
            ? t('velociraptor.orgTemplates.settingsSaving')
            : t('velociraptor.orgTemplates.settingsSave')}
        </button>
      </div>
    </div>
  )
}
